import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import apiService from '../../services/api';
import { useLanguage } from '../../contexts/LanguageContext';
import { useToast, useConfirm } from '../../contexts/ToastContext';
import { formatEGP } from '../../utils/format';
import { exportMonthlyClosingToExcel } from '../../utils/excelExport';
import { formatProductUnitType } from '../../utils/unitLabels';
import { getTokens } from '../../utils/tokens';
import { Calendar, Loader2, Download, Trash2, Eye, X } from 'lucide-react';
import {
  PageHeader, StatStrip, Toolbar, DataTable, Pagination, MonoMoney,
} from '../ui/AdminPagePrimitives';
import { StatStripSkeleton, TableSkeleton } from '../ui/Skeleton';

const MonthlyClosing = () => {
  const { mode } = useSelector((state) => state.theme);
  const { t, isRTL } = useLanguage();
  const showToast = useToast();
  const showConfirm = useConfirm();
  const tk = getTokens(mode);
  const { language } = useLanguage();

  const [closings, setClosings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [error, setError] = useState(null);
  const [closingDate, setClosingDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [optionalStartDate, setOptionalStartDate] = useState('');
  const [viewModal, setViewModal] = useState({ open: false, closing: null });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // ── style helpers ──────────────────────────────────────────────────────────
  const wrapperStyle = {
    background: tk.bg, color: tk.text, padding: 20, minHeight: '100%',
    fontFamily: language === 'ar' ? '"Cairo",sans-serif' : '"DM Sans",sans-serif',
    overflowY: 'auto',
  };
  const overlay = {
    position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50,
  };
  const modalBox = {
    background: tk.surface, border: `1px solid ${tk.border}`, borderRadius: 14,
    padding: 24, maxWidth: 500, width: '100%', margin: '0 16px',
    maxHeight: '90vh', overflowY: 'auto',
  };
  // ── data ───────────────────────────────────────────────────────────────────
  useEffect(() => { loadClosings(); setCurrentPage(1); }, []);

  const loadClosings = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiService.getMonthlyClosings();
      setClosings(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('monthlyClosing.loadFailed'));
      setClosings([]);
    } finally {
      setLoading(false);
    }
  };

  // ── pagination ──────────────────────────────────────────────────────────────
  const totalPages = Math.ceil(closings.length / itemsPerPage);
  const paginatedClosings = React.useMemo(() => {
    const start = (currentPage - 1) * itemsPerPage;
    return closings.slice(start, start + itemsPerPage);
  }, [closings, currentPage]);

  // ── handlers ───────────────────────────────────────────────────────────────
  const handleExport = async (c) => {
    setExportingId(c.id);
    try {
      const res = await apiService.getMonthlyClosingExportData(c.id);
      const data = res?.data;
      if (!data || !data.summary) throw new Error('Invalid export data');
      const dateStr = (c.closing_date || '').toString().slice(0, 10);
      const labels = t('monthlyClosing.excelExport');
      const excelLabels = {
        ...(typeof labels === 'object' && labels !== null ? labels : {}),
        formatUnitType: (u) => formatProductUnitType(u, t),
      };
      await exportMonthlyClosingToExcel(data, `monthly-closing-${dateStr}.xlsx`, excelLabels);
    } catch (err) {
      showToast(err.body?.error || err.message || t('monthlyClosing.downloadFailed'), "error");
    } finally {
      setExportingId(null);
    }
  };

  const handleDelete = async (id, closingLabel) => {
    if (!await showConfirm(t('monthlyClosing.deleteConfirm').replace('{date}', closingLabel), { confirmLabel: t('common.delete') || 'Delete', cancelLabel: t('common.cancel') || 'Cancel', type: 'danger' })) return;
    setDeletingId(id);
    try {
      await apiService.deleteMonthlyClosing(id);
      loadClosings();
    } catch (err) {
      showToast(err.body?.error || err.message || t('monthlyClosing.deleteFailed'), "error");
    } finally {
      setDeletingId(null);
    }
  };

  const handleCalculateAndSave = async () => {
    if (!closingDate?.trim()) { showToast(t('monthlyClosing.closingDateRequired'), "warning"); return; }
    const startTrim = optionalStartDate?.trim() || '';
    if (startTrim && startTrim > closingDate.trim()) {
      showToast(t('monthlyClosing.startDateAfterClosing'), "warning");
      return;
    }
    setSaving(true);
    try {
      const payload = { closing_date: closingDate };
      if (optionalStartDate?.trim()) payload.start_date = optionalStartDate.trim();
      await apiService.createMonthlyClosing(payload);
      loadClosings();
    } catch (err) {
      const errMsg = err.body?.error || err.message || '';
      const isAlreadyExists = errMsg.toLowerCase().includes('already exists');
      showToast(isAlreadyExists ? t('monthlyClosing.alreadyExists') : (errMsg || t('monthlyClosing.saveFailed')), "warning");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    if (!d) return '—';
    const date = typeof d === 'string' ? new Date(d) : d;
    return date.toLocaleDateString(isRTL ? 'ar-EG' : 'en-GB', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  // ── view modal ─────────────────────────────────────────────────────────────
  const ViewModalComp = ({ closing, onClose }) => {
    if (!closing) return null;
    const revenue = parseFloat(closing.total_sales_revenue || closing.sales_revenue || closing.total_revenue || 0);
    const expenses = parseFloat(closing.total_expenses || 0);
    return (
      <div style={overlay}>
        <div style={modalBox}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
            <div style={{ fontSize: 14, fontWeight: 600, color: tk.text }}>
              {t('monthlyClosing.title')}
            </div>
            <button onClick={onClose} style={{ width: 28, height: 28, borderRadius: 7, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
              <X size={14} />
            </button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
            {[
              { label: t('monthlyClosing.startDate'), value: formatDate(closing.start_date) },
              { label: t('monthlyClosing.closingDate'), value: formatDate(closing.closing_date) },
            ].map(({ label, value }) => (
              <div key={label} style={{ background: tk.surfaceAlt, borderRadius: 8, padding: '10px 12px' }}>
                <div style={{ fontSize: 10, color: tk.textMuted, textTransform: 'uppercase', letterSpacing: 0.4, marginBottom: 4 }}>{label}</div>
                <div style={{ fontSize: 12.5, color: tk.text, fontWeight: 500 }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {[
              { label: t('monthlyClosing.totalExpenses'), value: expenses, color: tk.dangerText },
              { label: t('monthlyClosing.totalPurchases'), value: parseFloat(closing.total_purchases || 0), color: tk.text },
              { label: t('monthlyClosing.totalRefunds'), value: parseFloat(closing.total_refunds || 0), color: tk.dangerText },
              { label: t('monthlyClosing.totalSales'), value: parseFloat(closing.total_sales || 0), color: tk.text },
              { label: t('monthlyClosing.totalSalesRevenue'), value: revenue, color: tk.text },
            ].map(({ label, value, color }) => (
              <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: `1px solid ${tk.border}` }}>
                <span style={{ fontSize: 12, color: tk.textSecondary }}>{label}</span>
                <MonoMoney amount={value} tk={tk} size={12.5} weight={600} color={color} />
              </div>
            ))}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, color: tk.textSecondary, cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5 }}>
              {t('common.close')}
            </button>
            <button onClick={() => { handleExport(closing); }} disabled={exportingId === closing.id} style={{ flex: 1, height: 36, borderRadius: 8, background: tk.accent, border: 'none', color: '#fff', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 600, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
              {exportingId === closing.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
              {t('monthlyClosing.download') || 'Download'}
            </button>
          </div>
        </div>
      </div>
    );
  };

  // ── render ─────────────────────────────────────────────────────────────────
  return (
    <div style={wrapperStyle} dir={isRTL ? 'rtl' : 'ltr'}>
      <PageHeader
        tk={tk}
        title={t('monthlyClosing.title') || t('nav.monthlyClosing')}
        subtitle={t('monthlyClosing.subtitle') || 'Monthly closing records'}
      />

      {error && (
        <div style={{ background: tk.dangerSoft, color: tk.dangerText, border: `1px solid ${tk.danger}33`, borderRadius: 8, padding: '10px 12px', marginBottom: 12, fontSize: 12 }}>
          {error}
        </div>
      )}

      {loading ? (
        <StatStripSkeleton tk={tk} count={1} />
      ) : (
        <StatStrip tk={tk} items={[
          { label: language === 'ar' ? 'إجمالي الإقفالات' : 'Total closings', value: String(closings.length) },
        ]} />
      )}

      <Toolbar
        tk={tk}
        filters={(
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1fr) auto',
              columnGap: 8,
              rowGap: 6,
              width: '100%',
              alignItems: 'end',
            }}
          >
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {t('monthlyClosing.startDate')}
                </span>
                <input
                  type="date"
                  value={optionalStartDate}
                  onChange={(e) => setOptionalStartDate(e.target.value)}
                  style={{
                    height: 34, padding: '0 10px',
                    background: tk.surface, border: `1px solid ${tk.border}`,
                    borderRadius: 8, color: tk.text, fontFamily: 'inherit',
                    fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box',
                  }}
                />
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 }}>
                <span style={{ fontSize: 10.5, color: tk.textMuted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.4 }}>
                  {t('monthlyClosing.closingDate')}
                </span>
                <input
                  type="date"
                  value={closingDate}
                  onChange={(e) => setClosingDate(e.target.value)}
                  style={{
                    height: 34, padding: '0 10px',
                    background: tk.surface, border: `1px solid ${tk.border}`,
                    borderRadius: 8, color: tk.text, fontFamily: 'inherit',
                    fontSize: 12, outline: 'none', width: '100%', boxSizing: 'border-box',
                  }}
                />
              </div>
              <button
                type="button"
                onClick={handleCalculateAndSave}
                disabled={saving}
                style={{
                  alignSelf: 'flex-end',
                  height: 34,
                  padding: '0 12px',
                  borderRadius: 8,
                  background: tk.accent,
                  border: 'none',
                  color: '#fff',
                  cursor: saving ? 'not-allowed' : 'pointer',
                  fontFamily: 'inherit',
                  fontSize: 12.5,
                  fontWeight: 600,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  opacity: saving ? 0.7 : 1,
                  whiteSpace: 'nowrap',
                }}
              >
                {saving ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Calendar size={13} />}
                {saving ? t('common.loading') : t('monthlyClosing.calculateAndSave')}
              </button>
            <span style={{ gridColumn: '1 / 2', fontSize: 10, color: tk.textMuted, lineHeight: 1.35 }}>
              {t('monthlyClosing.startDateOptionalHint')}
            </span>
          </div>
        )}
      />

      {loading ? (
        <TableSkeleton tk={tk} colWidths={[1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 1.5, 0.7]} />
      ) : (
        <DataTable
          tk={tk}
          emptyMessage={t('monthlyClosing.noClosings') || 'No monthly closings found'}
          columns={[
            { key: 'period', label: t('monthlyClosing.startDate') || 'Start' },
            { key: 'closing_date_col', label: t('monthlyClosing.closingDate') || 'Closing date' },
            { key: 'expenses', label: t('monthlyClosing.totalExpenses') || 'Expenses' },
            { key: 'total_purchases', label: t('monthlyClosing.totalPurchases') || 'Total Purchases' },
            { key: 'total_refunds', label: t('monthlyClosing.totalRefunds') || 'Refunds' },
            { key: 'total_sales', label: t('monthlyClosing.totalSales') || 'Sales' },
            { key: 'revenue', label: t('monthlyClosing.totalSalesRevenue') || 'Revenue' },
            { key: 'actions', label: '' },
          ]}
          rows={paginatedClosings}
          renderCell={(row, key) => {
            if (key === 'period') return (
              <span style={{ fontWeight: 600, color: tk.text, fontSize: 12.5 }}>{formatDate(row.start_date)}</span>
            );
            if (key === 'closing_date_col') return (
              <span style={{ fontSize: 12, color: tk.textSecondary }}>{formatDate(row.closing_date)}</span>
            );
            if (key === 'expenses') {
              return <MonoMoney abbreviate amount={parseFloat(row.total_expenses || 0)} tk={tk} size={12.5} weight={600} color={tk.dangerText} />;
            }
            if (key === 'total_purchases') {
              return <MonoMoney abbreviate amount={parseFloat(row.total_purchases || 0)} tk={tk} size={12.5} weight={600} />;
            }
            if (key === 'total_sales') {
              return <MonoMoney abbreviate amount={parseFloat(row.total_sales || 0)} tk={tk} size={12.5} weight={600} />;
            }
            if (key === 'revenue') {
              const rev = parseFloat(row.total_sales_revenue || row.sales_revenue || row.total_revenue || 0);
              return <MonoMoney abbreviate amount={rev} tk={tk} size={12.5} weight={600} />;
            }
            if (key === 'total_refunds') {
              return <MonoMoney abbreviate amount={parseFloat(row.total_refunds || 0)} tk={tk} size={12.5} weight={600} color={tk.dangerText} />;
            }
            if (key === 'actions') return (
              <div style={{ display: 'flex', gap: 4 }}>
                <button onClick={() => setViewModal({ open: true, closing: row })} style={{ width: 28, height: 28, background: tk.surfaceAlt, border: `1px solid ${tk.border}`, borderRadius: 6, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.textSecondary }}>
                  <Eye size={13} />
                </button>
                <button
                  onClick={() => handleExport(row)}
                  disabled={exportingId === row.id}
                  style={{ width: 28, height: 28, background: tk.accentSoft, border: `1px solid ${tk.accent}33`, borderRadius: 6, cursor: exportingId === row.id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.accentText, opacity: exportingId === row.id ? 0.6 : 1 }}
                >
                  {exportingId === row.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Download size={13} />}
                </button>
                <button
                  onClick={() => handleDelete(row.id, formatDate(row.closing_date))}
                  disabled={deletingId === row.id}
                  style={{ width: 28, height: 28, background: tk.dangerSoft, border: `1px solid ${tk.danger}33`, borderRadius: 6, cursor: deletingId === row.id ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: tk.dangerText, opacity: deletingId === row.id ? 0.6 : 1 }}
                >
                  {deletingId === row.id ? <Loader2 size={13} style={{ animation: 'spin 1s linear infinite' }} /> : <Trash2 size={13} />}
                </button>
              </div>
            );
            return <span style={{ color: tk.textSecondary, fontSize: 12 }}>{row[key] || '—'}</span>;
          }}
        />
      )}

      <Pagination tk={tk} currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />

      {viewModal.open && viewModal.closing && (
        <ViewModalComp closing={viewModal.closing} onClose={() => setViewModal({ open: false, closing: null })} />
      )}
    </div>
  );
};

export default MonthlyClosing;
